"use client";

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

/**
 * The port of das-count-up.js: a digit scramble for the DAS Academies v2 stats.
 *
 * Rolls the digits of a value like "£18.5bn" or "200K+" before settling them
 * left to right. Only characters 0-9 are touched, so currency symbols, suffixes
 * and words such as "1 in 3" survive untouched.
 *
 * Replays every time the section is scrolled back into view. Scrolling away
 * cancels whatever is mid-flight and restores the real text, so the values are
 * never left half scrambled. The real text is what is server-rendered, so it
 * is there if JS never runs.
 */

const DURATION = 1100;
const TICK = 45;
const DIGITS = "0123456789";

// Bumped each time the section comes into view; 0 while it is out of view.
const RunContext = createContext(0);

type SectionProps = {
  className: string;
  dataId?: string;
  children: ReactNode;
};

/** The section root (.js-das-count-up), which watches itself come into view. */
export function CountUpSection({ className, dataId, children }: SectionProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const [run, setRun] = useState(0);

  useEffect(() => {
    const section = sectionRef.current;

    if (!section || !("IntersectionObserver" in window)) {
      return;
    }

    let isRunning = false;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            isRunning = false;
            setRun(0);

            return;
          }

          if (isRunning) {
            return;
          }

          isRunning = true;
          setRun((count) => count + 1);
        });
      },
      { threshold: 0.15 },
    );

    observer.observe(section);

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className={className} data-id={dataId}>
      <RunContext.Provider value={run}>{children}</RunContext.Provider>
    </section>
  );
}

type ValueProps = {
  value: string;
  /** Milliseconds before this value starts, so a row of them ripples. */
  delay: number;
  className: string;
};

/** One value (data-count-up), scrambled into place on each run. */
export function CountUpValue({ value, delay, className }: ValueProps) {
  const run = useContext(RunContext);
  const [text, setText] = useState(value);

  useEffect(() => {
    if (!run) {
      return;
    }

    const characters = value.split("");

    // Where each digit sits, so they can be settled in reading order.
    const positions = characters.flatMap((character, index) =>
      /\d/.test(character) ? [index] : [],
    );

    if (!positions.length) {
      return;
    }

    let timer: ReturnType<typeof setInterval> | undefined;

    const timeout = setTimeout(() => {
      const started = Date.now();

      timer = setInterval(() => {
        const progress = (Date.now() - started) / DURATION;

        if (progress >= 1) {
          clearInterval(timer);
          setText(value);

          return;
        }

        const frame = characters.map((character, index) => {
          if (!/\d/.test(character)) {
            return character;
          }

          const order = positions.indexOf(index);
          const settleAt =
            positions.length === 1
              ? 0.6
              : 0.3 + (order / (positions.length - 1)) * 0.55;

          return progress >= settleAt
            ? character
            : DIGITS.charAt(Math.floor(Math.random() * 10));
        });

        setText(frame.join(""));
      }, TICK);
    }, delay);

    // Drop any pending timers and put the real value back.
    return () => {
      clearTimeout(timeout);
      clearInterval(timer);
      setText(value);
    };
  }, [run, value, delay]);

  return (
    <p className={className} data-count-up={value} data-count-delay={delay}>
      {text}
    </p>
  );
}
